import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Monitor } from "lucide-react";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import JeaBadge from "@/components/jea/JeaBadge";
import Skeleton from "@/components/jea/Skeleton";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { relativeTime } from "@/lib/dates";
import {
  ContactMethodsPanel,
  TwoFactorMethodsPanel,
  Section,
} from "./ContactAnd2faPanel";

const EVENT_LABELS = {
  "auth.login": "Login",
  "auth.logout": "Logout",
  "auth.2fa_verified": "2FA Verified",
  "auth.failed_login": "Failed Login",
  "auth.password_changed": "Password Changed",
  "auth.session_revoked": "Session Revoked",
  "auth.account_deleted": "Account Deleted",
  "profile.email_changed": "Email Changed",
  "profile.name_changed": "Name Changed",
};

const SecurityPanel = () => {
  const [sessions, setSessions] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revokingId, setRevokingId] = useState(null);

  const load = async () => {
    try {
      const [s, h] = await Promise.all([
        apiClient.get("/users/me/sessions"),
        apiClient.get("/users/me/login-history"),
      ]);
      setSessions(s.data.sessions || []);
      setHistory(h.data.events || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const revoke = async (id) => {
    setRevokingId(id);
    try {
      await apiClient.delete(`/users/me/sessions/${id}`);
      toast.success("Session revoked.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRevokingId(null); }
  };

  return (
    <JeaCard data-testid="security-panel">
      <Section label="Contact Methods" testid="security-contact-methods">
        <ContactMethodsPanel />
      </Section>

      <Section label="Two-Factor Authentication Methods" testid="security-2fa-methods">
        <TwoFactorMethodsPanel />
      </Section>

      <Section label="Session Management" testid="security-sessions">
        <p className="text-text-secondary text-[13px] mb-4">
          Active sessions across all your devices. Sign out remotely to invalidate a session.
        </p>
        {loading ? (
          <Skeleton className="h-24" />
        ) : sessions.length === 0 ? (
          <div className="text-text-muted text-[13px]" data-testid="sessions-empty">No active sessions found.</div>
        ) : (
          <div className="space-y-2" data-testid="sessions-list">
            {sessions.map((s) => (
              <div
                key={s.id}
                className="flex items-center gap-4 bg-bg-primary border border-border-default rounded-[8px] p-4"
                data-testid={`session-row-${s.id}`}
              >
                <Monitor size={18} className="text-text-secondary shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-white text-[14px] font-semibold">{s.device || "Unknown device"}</div>
                    {s.is_current && (
                      <JeaBadge variant="success" data-testid={`session-current-${s.id}`}>Current session</JeaBadge>
                    )}
                  </div>
                  <div className="text-text-secondary text-[12px] mt-0.5 jea-mono">
                    {s.ip || "—"} · {s.last_active_at ? relativeTime(s.last_active_at) : "—"}
                  </div>
                </div>
                {s.is_current ? (
                  <span className="text-text-muted text-[12px]">This session</span>
                ) : (
                  <JeaButton
                    size="sm"
                    variant="outline-neutral"
                    onClick={() => revoke(s.id)}
                    loading={revokingId === s.id}
                    data-testid={`session-revoke-${s.id}`}
                  >
                    Sign Out
                  </JeaButton>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section label="Login History" testid="security-history">
        <p className="text-text-secondary text-[13px] mb-4">Last 20 authentication events.</p>
        {loading ? (
          <Skeleton className="h-24" />
        ) : history.length === 0 ? (
          <div className="text-text-muted text-[13px]" data-testid="history-empty">No events yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="history-table">
              <thead>
                <tr className="text-left border-b border-border-default">
                  {["Timestamp", "Event", "IP", "Device"].map((h) => (
                    <th key={h} className="jea-caption text-text-secondary p-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((e) => (
                  <tr key={e.id} className="border-b border-border-default" data-testid={`history-row-${e.id}`}>
                    <td className="p-3 jea-date text-text-secondary text-[13px]">
                      {e.created_at ? new Date(e.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="p-3 text-white text-[13px]">
                      {EVENT_LABELS[e.type] || e.type}
                    </td>
                    <td className="p-3 jea-mono text-text-secondary text-[12px]">{(e.metadata || {}).ip || "—"}</td>
                    <td className="p-3 text-text-secondary text-[13px]">{(e.metadata || {}).device || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </JeaCard>
  );
};

export default SecurityPanel;
